// Компараторы колонок для гридов сущностей.
//
// Сортировка была у четырёх колонок из восьми списков, и каждая писала свой
// компаратор: где-то `localeCompare` без локали, где-то вычитание дат, где-то
// `Infinity` вместо пустого значения. Здесь одно правило на все гриды.
//
// Пустое значение всегда стоит после заполненного — и по возрастанию, и по
// убыванию. Иначе первая страница «по сроку» показывала бы записи без срока,
// то есть ровно те, которые в сортировке по сроку не нужны. antd переворачивает
// знак компаратора при `descend`, поэтому знак для пустых компенсируется по
// третьему аргументу.
import type { SortOrder } from 'antd/es/table/interface';

export type Comparator<T> = (a: T, b: T, sortOrder?: SortOrder) => number;

/** Знак, который держит пустое значение внизу при обоих направлениях. */
function missingLast(sortOrder: SortOrder | undefined): number {
  return sortOrder === 'descend' ? -1 : 1;
}

/**
 * Сравнение по заполненности. `null` — оба значения на месте, сравнивать дальше.
 */
function compareMissing(
  aMissing: boolean,
  bMissing: boolean,
  sortOrder: SortOrder | undefined,
): number | null {
  if (aMissing && bMissing) return 0;
  if (aMissing) return missingLast(sortOrder);
  if (bMissing) return -missingLast(sortOrder);
  return null;
}

/** Текст: по-русски, без учёта регистра и диакритики. */
export function byText<T>(pick: (row: T) => string | null | undefined): Comparator<T> {
  return (a, b, sortOrder) => {
    const left = pick(a)?.trim() ?? '';
    const right = pick(b)?.trim() ?? '';
    const missing = compareMissing(left.length === 0, right.length === 0, sortOrder);
    if (missing !== null) return missing;
    return left.localeCompare(right, 'ru', { sensitivity: 'base' });
  };
}

/** Число; API отдаёт суммы строкой (Decimal), поэтому строка тоже принимается. */
export function byNumber<T>(pick: (row: T) => number | string | null | undefined): Comparator<T> {
  return (a, b, sortOrder) => {
    const left = toNumber(pick(a));
    const right = toNumber(pick(b));
    const missing = compareMissing(left === null, right === null, sortOrder);
    if (missing !== null) return missing;
    return (left as number) - (right as number);
  };
}

/** Дата: ISO-строка или `Date`. Нераспознанное значение считается пустым. */
export function byDate<T>(pick: (row: T) => string | Date | null | undefined): Comparator<T> {
  return (a, b, sortOrder) => {
    const left = toTime(pick(a));
    const right = toTime(pick(b));
    const missing = compareMissing(left === null, right === null, sortOrder);
    if (missing !== null) return missing;
    return (left as number) - (right as number);
  };
}

/**
 * Порядок по заранее заданному списку значений — для колонок вроде стадии или
 * статуса, где алфавит не имеет смысла («Won» не идёт после «New»).
 * Значение вне списка считается пустым и уходит вниз.
 */
export function byOrder<T, TValue extends string>(
  pick: (row: T) => TValue | null | undefined,
  order: readonly TValue[],
): Comparator<T> {
  return (a, b, sortOrder) => {
    const left = indexIn(order, pick(a));
    const right = indexIn(order, pick(b));
    const missing = compareMissing(left === null, right === null, sortOrder);
    if (missing !== null) return missing;
    return (left as number) - (right as number);
  };
}

function toNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toTime(value: string | Date | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

function indexIn<TValue extends string>(
  order: readonly TValue[],
  value: TValue | null | undefined,
): number | null {
  if (value === null || value === undefined) return null;
  const index = order.indexOf(value);
  return index === -1 ? null : index;
}
